import { mapboxToken } from "@/constants";
import polygonDrawCursor from "@/assets/images/polygon-draw-cursor.svg?url";
import {
  BAM_MAP_STYLE_SOURCES,
  BAM_MAP_STYLES,
  defaultZoomLevel,
} from "@/constants/mapStyles";
import {
  selectIsLeaseCompsDrawerOpen,
  selectLeaseCompMediaFiles,
  selectLeaseComps,
} from "@/features/survey/leaseCompsSelectors";
import {
  setCurrentLeaseCompId,
  setOpenLeaseCompsDrawer,
  updateLeaseComp as updateLeaseCompInStore,
} from "@/features/survey/leaseCompsSlice";
import {
  selectPOICategories,
  selectPOIs,
} from "@/features/survey/poisSelectors";
import {
  selectVisiblePolygons,
  selectIsPolygonDrawingMode,
} from "@/features/survey/polygonSelectors";
import { updatePOI as updatePOIInStore } from "@/features/survey/poisSlice";
import { selectMarketMapKeys } from "@/features/survey/surveySelectors";
import {
  useUpdateLeaseCompMutation,
  useUpdateLeaseCompVisibilityMutation,
} from "@/services/leaseComps";
import { useFetchMarinePortsQuery } from "@/services/mapApi";
import {
  useCreatePolygonMutation,
  useDeletePolygonMutation,
  useUpdatePolygonMutation,
} from "@/services/survey-polygons";
import {
  useUpdateSurveyFlagsMutation,
} from "@/services/survey-core";
import {
  useAddSurveyMapKeysMutation,
  useUpdateSurveyMapKeyMutation,
} from "@/services/survey-mapKeys";
import { useUpdatePOIMutation } from "@/services/survey-poi";
import { useUpdatePropertyMutation } from "@/services/survey-properties";
import {
  useIsPOIsDrawerOpen,
  useIsPropertiesDrawerOpen,
} from "@/shared/hooks/useSurveySelectors";
import { isEmptyObjectFromStr } from "@/shared/utils";
import * as mapUtil from "@/shared/utils/map";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import MapCenter from "@features/survey/components/mapControls/MapCenter";
import MapKey from "@features/survey/components/mapControls/MapKey";
import MapRestore from "@features/survey/components/mapControls/MapRestore";
import MapStyles from "@features/survey/components/mapControls/MapStyles";
import MapStyleDrawer from "@features/survey/components/preview/MapStyleDrawer";
import PolygonStyleDrawer from "@features/survey/components/mapControls/PolygonStyleDrawer";
import {
  setStyleEditInProgress,
  updateToggleLabelMapKey,
  addPolygon,
  updatePolygonInStore,
  deletePolygonFromStore,
  setCurrentPolygonId,
  setPolygonDrawingMode,
} from "@features/survey/surveySlice";
import { CustomMarker } from "@features/survey/types/survey.mapInterface.types";
import { DetailedSurvey } from "@survey-types/survey.core.types";
import type { MapKey as MapKeyType } from "@survey-types/survey.mapkeys.types";
import type { MapPolygon, PolygonStyle } from "@survey-types/survey.polygon.types";
import { DEFAULT_POLYGON_STYLE } from "@survey-types/survey.polygon.types";
import { Property } from "@survey-types/survey.property.types";
import {
  currentPoiId,
  currentPropertyId as currentPropertyIdAction,
  setOpenPOIsDrawer,
  setOpenPropertiesDrawer,
  updateCurrentSurvey,
  updateProperty as updatePropertyAction,
} from "@survey/surveySlice";
import { debounce } from "lodash";
import isEqual from "lodash/isEqual";
import mapboxgl from "mapbox-gl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import LocationAdjustmentModal from "./LocationAdjustmentModal";

interface RadiusObj {
  value: number;
}

interface MapPoints {
  features: Array<{ id: string }>;
}

interface MarketMapPreviewProps {
  index: number;
  isClientShortList?: boolean;
}

const POLYGON_DRAW_COLOR_OPTIONS: string[] = [
  "#d43545",
  "#e47444",
  "#ed5f8a",
  "#7e76dd",
  "#a23f97",
  "#41afca",
  "#0976c3",
  "#109335",
  "#6ca428",
  "#f9dd4b",
];

const getNextPolygonStyle = (existingPolygons: MapPolygon[]): PolygonStyle => {
  const usedFillColors = new Set(
    existingPolygons
      .map((polygon) => polygon.style.fillColor?.toLowerCase())
      .filter(Boolean),
  );

  const uniqueColor = POLYGON_DRAW_COLOR_OPTIONS.find(
    (color) => !usedFillColors.has(color.toLowerCase()),
  );

  const fallbackColor =
    POLYGON_DRAW_COLOR_OPTIONS[
      existingPolygons.length % POLYGON_DRAW_COLOR_OPTIONS.length
    ] || DEFAULT_POLYGON_STYLE.fillColor;

  const color = uniqueColor || fallbackColor;

  return {
    ...DEFAULT_POLYGON_STYLE,
    fillColor: color,
    strokeColor: color,
  };
};

const MarketMapPreview = (props: MarketMapPreviewProps) => {
  const { index, isClientShortList } = props;
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const survey = useAppSelector((state) => state.survey.currentSurvey);
  const selectMediaFiles = useAppSelector((state) => state.survey.mediaFiles);
  const isIncludeMapKey = useAppSelector(
    (state) => state.survey?.currentSurvey?.isIncludeMapKey,
  );
  const isIncludeLeaseComps = useAppSelector(
    (state) => state.survey?.currentSurvey?.isIncludeLeaseComps,
  );
  const isIncludePOI = useAppSelector(
    (state) => state.survey?.currentSurvey?.isIncludePOI,
  );
  const isIncludePolygons = useAppSelector(
    (state) => state.survey?.currentSurvey?.isIncludePolygons,
  );
  const getComps = useAppSelector(selectLeaseComps);
  const leaseComps = useMemo(
    () => (isIncludeLeaseComps ? getComps : []),
    [isIncludeLeaseComps, getComps],
  );
  const leaseCompMediaFiles = useAppSelector(selectLeaseCompMediaFiles);
  const isLeaseCompsDrawerOpen = useAppSelector(selectIsLeaseCompsDrawerOpen);
  const isPropertiesDrawerOpen = useIsPropertiesDrawerOpen();
  const isPOIsDrawerOpen = useIsPOIsDrawerOpen();
  const latestProperty = useRef<Property[]>(survey?.properties);
  const surveyRef = useRef<DetailedSurvey>(survey);
  // Mirrors leaseComps in a ref so useCallback/useMemo deps don't include leaseComps.
  // This prevents debouncedUpdateLeaseCompStyle from being recreated (and its pending
  // timer cancelled) whenever the leaseComps array refetches after a coordinate update.
  const latestLeaseComps = useRef<typeof leaseComps>(leaseComps);
  latestLeaseComps.current = leaseComps;
  const bingMap = useAppSelector((state) => state.survey.currentBingMap);
  const dispatch = useAppDispatch();
  const [updateSurveyFlags] = useUpdateSurveyFlagsMutation();
  const [updateProperty] = useUpdatePropertyMutation();
  const [updateLeaseComp] = useUpdateLeaseCompMutation();
  const [updateVisibility] = useUpdateLeaseCompVisibilityMutation();
  const [updateSurveyMapKey] = useUpdateSurveyMapKeyMutation();
  const [addSurveyMapKeys] = useAddSurveyMapKeysMutation();
  const [updatePOI] = useUpdatePOIMutation();
  const [createPolygon] = useCreatePolygonMutation();
  const [patchPolygon] = useUpdatePolygonMutation();
  const [removePolygon] = useDeletePolygonMutation();
  const mapKeys = useSelector(selectMarketMapKeys);
  const { data: marinePortsRes } = useFetchMarinePortsQuery();
  // POI categories are fetched at Builder.tsx level to avoid duplicate requests
  // Use Redux state for rendering (automatically updated by the top-level query via onQueryStarted)
  const poiCategories = useAppSelector(selectPOICategories);
  // Use Redux state for POI data (updated by optimistic updates)
  const poiPoints = useAppSelector(selectPOIs);
  const latestPOIs = useRef<typeof poiPoints>(poiPoints);

  // Polygon state
  const polygons = useAppSelector(selectVisiblePolygons);
  const shouldRenderPolygons = Boolean(isIncludePolygons);
  const isPolygonDrawingMode = useAppSelector(selectIsPolygonDrawingMode);
  const [currentDrawingPolygon, setCurrentDrawingPolygon] = useState<number[][]>([]);
  const [selectedPolygon, setSelectedPolygon] = useState<MapPolygon | null>(null);
  const [isPolygonStyleOpen, setIsPolygonStyleOpen] = useState(false);

  const [pinsDragged, setPinsDragged] = useState(false);
  const [markerInstance, setMarkerInstance] = useState<CustomMarker | null>();
  const [markerPoint, setMarkerPoint] = useState<any>(null);
  const [openMapStyleDrawer, setOpenMapStyleDrawer] = useState(false);
  const [isMapStyle, setIsMapStyle] = useState(false);
  const [isMapEdit, setIsMapEdit] = useState(false);
  const [portsData, setPortsData] = useState<any[]>([]);
  const [selectedPinInfo, setSelectedPinInfo] = useState<{
    numbers: number[];
    ids: string[];
    type: string;
  } | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [pendingLocationUpdate, setPendingLocationUpdate] = useState<{
    propId: string;
    latLng: { lng: number; lat: number };
    data: any;
    isLeaseComp?: boolean;
    isPOI?: boolean;
  } | null>(null);
  const previousPropertyCountRef = useRef<number>(
    survey?.properties?.length || 0,
  );
  const shouldRecenterRef = useRef<boolean>(false);
  const isLocationModalOpenRef = useRef<boolean>(false);
  const isStyleEditInProgressRef = useRef<boolean>(false);
  const mapKeysRef = useRef(mapKeys);
  const leaseCompsRef = useRef(leaseComps);
  const prevIsIncludeMapKeyRef = useRef<boolean | undefined>(undefined);
  const isFitBoundsInProgressRef = useRef<boolean>(false);
  const idleListenerRef = useRef<(() => void) | null>(null);
  const shouldRecenterAfterPolygonDeleteRef = useRef<boolean>(false);

  // Polygon event handler refs
  const polygonMouseEnterHandlerRef = useRef<(() => void) | null>(null);
  const polygonMouseLeaveHandlerRef = useRef<(() => void) | null>(null);
  const polygonMouseLeaveCursorRef = useRef("");
  polygonMouseLeaveCursorRef.current = isPolygonDrawingMode
    ? `url("${polygonDrawCursor}") 12 28, crosshair`
    : "";
  // Refs for stable drawing callbacks (prevents stale closures)
  const isPolygonDrawingModeRef = useRef(isPolygonDrawingMode);
  const currentDrawingPolygonRef = useRef(currentDrawingPolygon);
  const polygonsRef = useRef(polygons);
  // Synchronous mutable ref — updated on every click so dblclick handler always
  // has the full up-to-date point list regardless of React's render batching.
  const drawingPointsRef = useRef<number[][]>([]);
  // Tracks in-flight POST promises keyed by tempId so PATCH calls can wait
  // for the real polygonId before hitting the backend.
  const pendingPostsRef = useRef<Map<string, Promise<string>>>(new Map());

  if (!survey) {
    return null;
  }

  const [marketMapData, setMarketMapData] = useState<any>(() => {
    if (
      survey.marketMapBamData &&
      typeof JSON.parse(survey.marketMapBamData) === "object" &&
      !isEmptyObjectFromStr(survey.marketMapBamData)
    ) {
      return JSON.parse(survey.marketMapBamData);
    }
    return {
      mapCentroid: mapUtil.getCenterPoint(latestProperty.current || []),
      mapZoomLevel: defaultZoomLevel,
    };
  });

  useEffect(() => {
    if (!mapboxToken) {
      console.error("Mapbox token is not defined");
      return;
    }

    mapboxgl.accessToken = mapboxToken;
    const style =
      surveyRef.current?.marketMapStyle === BAM_MAP_STYLES.emeraldBingHybrid
        ? bingMap
        : BAM_MAP_STYLE_SOURCES[
            surveyRef?.current?.marketMapStyle ||
              Object.keys(BAM_MAP_STYLE_SOURCES)[0]
          ];
    if (mapContainerRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: style || BAM_MAP_STYLE_SOURCES.cbreLight,
        center: marketMapData.mapCentroid || [-95, 40],
        zoom: marketMapData.mapZoomLevel || defaultZoomLevel,
        // attributionControl: false,
        preserveDrawingBuffer: true,
      });
      if (index === 0) {
        mapRef.current.addControl(
          new mapboxgl.NavigationControl(),
          "top-right",
        );
        mapRef.current.addControl(new mapboxgl.ScaleControl(), "bottom-left");
      } else {
        mapRef.current.dragPan.disable();
        mapRef.current.dragRotate.disable();
        mapRef.current.scrollZoom.disable();
        mapRef.current.boxZoom.disable();
        mapRef.current.touchZoomRotate.disable();
      }
      mapUtil
        .getMapPointsWithImages(
          survey,
          selectMediaFiles ?? undefined,
          leaseComps,
          leaseCompMediaFiles,
        )
        .then(async (points: any) => {
          if (mapRef?.current && points) {
            // Add POI features to the points
            if (isIncludePOI && poiPoints && poiCategories) {
              const poiFeatures = mapUtil.createPOIFeatures(
                poiPoints,
                poiCategories,
              );
              points.features.push(...poiFeatures);
            }
            await mapUtil.getMarkerConfig(points, mapRef.current);
            mapEventActions();
          }
        });
      mapRef.current?.resize();
      mapRef.current.on("dragend", dragAndZoomHandler);
      mapRef.current.on("zoomend", dragAndZoomHandler);
      const surveyBamData = JSON.parse(
        surveyRef.current?.marketMapBamData || "{}",
      );
      if (
        !surveyBamData?.mapReset &&
        surveyBamData?.mapCentroid &&
        surveyBamData?.mapZoomLevel
      ) {
        updateMapView(
          marketMapData.mapCentroid || [-95, 40],
          marketMapData.mapZoomLevel || defaultZoomLevel,
        );
      } else {
        fitBoundsForMapReset();
      }

      const movedProperties = mapUtil.getMovedPropertyPinIds(survey.properties);
      const movedLeaseCompPins = mapUtil.getMovedLeaseCompPinIds(
        leaseComps || [],
      );
      const movedPOIPins = mapUtil.getMovedPOIPinIds(poiPoints || []);
      if (
        movedProperties.length > 0 ||
        movedLeaseCompPins.length > 0 ||
        movedPOIPins.length > 0
      ) {
        setPinsDragged(true);
      } else {
        setPinsDragged(false);
      }
    }
    return () => {
      const map = mapRef.current;
      if (map) {
        // Clean up idle listener if one is registered
        if (idleListenerRef.current) {
          map.off("idle", idleListenerRef.current);
          idleListenerRef.current = null;
        }
        map.off("dragend", dragAndZoomHandler);
        map.off("zoomend", dragAndZoomHandler);
        const { _markers: markers } = map;
        markers?.forEach((_marker: mapboxgl.Marker) => {
          _marker.off("dragend", dragAndZoomHandler);
        });
        map.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    surveyRef.current = survey;
    latestProperty.current = survey.properties || [];
  }, [survey]);

  // Sync polygon refs every render so callbacks always see fresh state
  isPolygonDrawingModeRef.current = isPolygonDrawingMode;
  currentDrawingPolygonRef.current = currentDrawingPolygon;
  polygonsRef.current = polygons;

  // Check for selected pin info when style drawer opens
  useEffect(() => {
    if (openMapStyleDrawer && isMapStyle) {
      const storedInfo = sessionStorage.getItem("selectedPinInfo");
      if (storedInfo) {
        try {
          setSelectedPinInfo(JSON.parse(storedInfo));
          // Clear after reading
          sessionStorage.removeItem("selectedPinInfo");
        } catch (e) {
          console.error("Failed to parse selectedPinInfo", e);
          setSelectedPinInfo(null);
        }
      } else {
        // No stored info, clear any existing selection
        setSelectedPinInfo(null);
      }
    } else if (!openMapStyleDrawer) {
      // Clear selected pin info when drawer closes
      setSelectedPinInfo(null);
      sessionStorage.removeItem("selectedPinInfo");
    }
  }, [openMapStyleDrawer, isMapStyle]);

  // Close style drawer when properties drawer opens
  useEffect(() => {
    if (isPropertiesDrawerOpen && openMapStyleDrawer) {
      setOpenMapStyleDrawer(false);
      setIsMapStyle(false);
      setSelectedPinInfo(null);
      sessionStorage.removeItem("selectedPinInfo");
    }
  }, [isPropertiesDrawerOpen]);

  // Close style drawer when lease comps drawer opens
  useEffect(() => {
    if (isLeaseCompsDrawerOpen && openMapStyleDrawer) {
      setOpenMapStyleDrawer(false);
      setIsMapStyle(false);
      setSelectedPinInfo(null);
      sessionStorage.removeItem("selectedPinInfo");
    }
  }, [isLeaseCompsDrawerOpen]);

  useEffect(() => {
    latestPOIs.current = poiPoints;
  }, [poiPoints]);

  useEffect(() => {
    mapKeysRef.current = mapKeys;
  }, [mapKeys]);

  useEffect(() => {
    leaseCompsRef.current = leaseComps;
  }, [leaseComps]);

  useEffect(() => {
    if (!mapRef.current) return;
    // Re-render map points when lease comps visibility changes
    setIsMapEdit(false);
    updateMapPoints();
  }, [isIncludeLeaseComps]);

  useEffect(() => {
    const movedProperties = mapUtil.getMovedPropertyPinIds(survey.properties);
    const movedLeaseCompPins = mapUtil.getMovedLeaseCompPinIds(
      leaseComps || [],
    );
    const movedPOIPins = mapUtil.getMovedPOIPinIds(poiPoints || []);
    setPinsDragged(
      movedProperties.length > 0 ||
        movedLeaseCompPins.length > 0 ||
        movedPOIPins.length > 0,
    );

    const currentPropertyCount = survey.properties?.length || 0;
    const wasPropertyRemoved =
      currentPropertyCount < previousPropertyCountRef.current;

    if (wasPropertyRemoved && index === 0) {
      // Bypass debounce for immediate update when a property is removed
      shouldRecenterRef.current = true;
      updateMapPoints(survey);
    } else if (
      !isStyleEditInProgressRef.current &&
      ((!openMapStyleDrawer &&
        !isMapEdit &&
        !showLocationModal &&
        !isLocationModalOpenRef.current) ||
        index !== 0)
    ) {
      renderMapHandler();
    }

    previousPropertyCountRef.current = currentPropertyCount;
  }, [
    selectMediaFiles,
    survey.properties,
    leaseComps,
    leaseCompMediaFiles,
    poiPoints,
    poiCategories,
    showLocationModal,
  ]);

  useEffect(() => {
    if (index === 0) return;
    const style =
      survey.marketMapStyle === BAM_MAP_STYLES.emeraldBingHybrid
        ? bingMap
        : BAM_MAP_STYLE_SOURCES[survey.marketMapStyle];
    if (style && mapRef.current) {
      mapRef.current.setStyle(style);
      mapRef.current.once("idle", () => {
        portsData?.length > 1 &&
          mapRef?.current &&
          mapUtil.addMarinePortLayer(
            mapRef.current,
            portsData,
            survey?.isMarinePorts ?? false,
          );
        mapRef.current && mapUtil.raisePolygonLayers(mapRef.current);
      });
    }
  }, [survey.marketMapStyle]);

  useEffect(() => {
    if (index === 0 || !mapRef.current) return;

    const mapData = JSON.parse(survey.marketMapBamData || "{}");
    if (mapData.mapReset) {
      fitBoundsForMapReset();
    } else if (mapData.mapCentroid && mapData.mapZoomLevel) {
      updateMapView(mapData.mapCentroid, mapData.mapZoomLevel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, survey.marketMapBamData]);

  useEffect(() => {
    if (!marinePortsRes) return;
    fetchGeoJson();
  }, [marinePortsRes]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!isIncludePOI) {
      // If POI is disabled, still re-render to remove POI markers
      if (poiCategories || poiPoints) {
        updateMapPoints();
      }
      return;
    }
    if (!poiCategories || !poiPoints) return;
    // Re-render map points when POI data changes or visibility toggles
    updateMapPoints();
  }, [poiCategories, poiPoints, isIncludePOI]);

  useEffect(() => {
    if (!isPropertiesDrawerOpen && !isLeaseCompsDrawerOpen && !isPOIsDrawerOpen)
      return;
    setIsMapEdit(false);
  }, [isPropertiesDrawerOpen, isLeaseCompsDrawerOpen, isPOIsDrawerOpen]);

  useEffect(() => {
    // Skip on first render to avoid double-fitting on load
    if (prevIsIncludeMapKeyRef.current === undefined) {
      prevIsIncludeMapKeyRef.current = isIncludeMapKey;
      return;
    }
    prevIsIncludeMapKeyRef.current = isIncludeMapKey;
    fitBoundsForMapReset();
  }, [isIncludeMapKey]);

  useEffect(() => {
    fitBoundsForMapReset();
  }, [isIncludeMapKey]);

  const updateMapPoints = (
    modifiedSurvey = surveyRef.current,
    onComplete?: () => void,
    leaseCompsOverride = leaseComps,
  ) => {
    const mapComp = mapRef.current;
    if (mapComp) {
      mapUtil.deleteMarkers(mapComp);

      // Filter survey properties if isClientShortList is true
      let filteredSurvey = modifiedSurvey;
      if (isClientShortList && modifiedSurvey) {
        filteredSurvey = {
          ...modifiedSurvey,
          properties:
            modifiedSurvey.properties?.filter(
              (property) => property.isClientShortlist,
            ) || [],
        };
      }

      mapUtil
        .getMapPointsWithImages(
          filteredSurvey!,
          selectMediaFiles ?? undefined,
          leaseCompsOverride,
          leaseCompMediaFiles,
        )
        .then(async (points: any) => {
          // Add POI features to the points
          if (isIncludePOI && poiPoints && poiCategories) {
            const poiFeatures = mapUtil.createPOIFeatures(
              poiPoints,
              poiCategories,
            );
            points.features.push(...poiFeatures);
          }

          if (points.features.length > 0 && mapComp.getContainer()) {
            await mapUtil.getMarkerConfig(points, mapComp, true);
            const surveyBamData = JSON.parse(
              modifiedSurvey?.marketMapBamData || "{}",
            );

            // Check if we should re-center after property removal
            if (shouldRecenterRef.current && index === 0) {
              shouldRecenterRef.current = false;
              const mapData = { ...marketMapData, mapReset: true };
              setMarketMapData(mapData);

              // Wait for map to be idle before fitting bounds
              registerIdleFitBoundsListener(mapComp);

              dispatch(
                updateCurrentSurvey({
                  marketMapBamData: JSON.stringify(mapData),
                }),
              );
            } else if (
              shouldRenderPolygons &&
              polygonsRef.current.length > 0
            ) {
              registerIdleFitBoundsListener(mapComp);
            } else if (
              surveyBamData.mapReset ||
              !surveyBamData.mapCentroid ||
              !surveyBamData.mapZoomLevel
            ) {
              registerIdleFitBoundsListener(mapComp);
            } else {
              updateMapView(
                surveyBamData.mapCentroid || [-95, 40],
                surveyBamData.mapZoomLevel || defaultZoomLevel,
              );
            }
            mapEventActions();

            onComplete?.();
          } else {
            if (
              shouldRenderPolygons &&
              polygonsRef.current.length > 0
            ) {
              registerIdleFitBoundsListener(mapComp);
            }
            onComplete?.();
          }
        });
    }
  };

  const renderMapHandler = debounce(updateMapPoints, 500);

  const dragAndZoomHandler = (e: any) => {
    if (
      index !== 0 ||
      isPropertiesDrawerOpen ||
      isLeaseCompsDrawerOpen ||
      isPOIsDrawerOpen ||
      (e && !e.originalEvent)
    )
      return;
    setIsMapEdit(true);
    const mapCenter = mapRef?.current?.getCenter();
    const mapZoomLevel = mapRef?.current?.getZoom();
    const mapData = {
      ...marketMapData,
      mapReset: false,
      mapCentroid: [mapCenter?.lng, mapCenter?.lat],
      mapZoomLevel,
    };
    setMarketMapData(mapData);
    const isMapDataEqual = isEqual(
      mapData,
      JSON.parse(surveyRef.current?.marketMapBamData || "{}"),
    );
    if (!isMapDataEqual) {
      dispatch(
        updateCurrentSurvey({ marketMapBamData: JSON.stringify(mapData) }),
      );
      updateSurveyFlags({
        id: survey.id,
        marketMapBamData: JSON.stringify(mapData),
      });
    }
  };

  const mapEventActions = async () => {
    const mapStateUpdated = async (
      propId: string,
      latLng: { lng: Number; lat: Number },
      propertyType?: string,
    ) => {
      const mapCenter = mapRef?.current?.getCenter();
      const mapZoomLevel = mapRef?.current?.getZoom();
      const mapData = {
        ...marketMapData,
        mapReset: false,
        mapCentroid: [mapCenter?.lng, mapCenter?.lat],
        mapZoomLevel,
      };
      setMarketMapData(mapData);
      setPinsDragged(true);
      if (propertyType === "leaseComp") {
        // Use ref so we always read the freshest mapData (incl. any style changes
        // that were dispatched to Redux but whose debounced API call hasn't fired yet).
        const leaseComp = latestLeaseComps.current?.find(
          (each) => each.leaseCompId === propId,
        );
        if (leaseComp) {
          const leaseMapData = {
            ...leaseComp.mapData,
            latitude: String(latLng.lat),
            longitude: String(latLng.lng),
          };

          // Update Redux state immediately to keep marker at new position
          dispatch(
            updateLeaseCompInStore({
              leaseCompId: propId,
              updates: { mapData: leaseMapData },
            }),
          );

          // Store pending update and show confirmation modal
          isLocationModalOpenRef.current = true;
          setPendingLocationUpdate({
            propId,
            latLng: { lng: Number(latLng.lng), lat: Number(latLng.lat) },
            data: leaseMapData,
            isLeaseComp: true,
          });
          setShowLocationModal(true);
        }
      } else if (propertyType === "poi") {
        const poiId = propId.replace("poi-", "");
        const poi = latestPOIs.current?.find((p) => p.extId === poiId);
        if (poi && survey?.id) {
          const poiMapData = {
            ...poi.mapData,
            latitude: String(latLng.lat),
            longitude: String(latLng.lng),
          };

          // Update Redux state immediately to keep marker at new position
          dispatch(
            updatePOIInStore({
              extId: poiId,
              updates: { mapData: poiMapData },
            }),
          );

          // Also update latestPOIs ref immediately to prevent marker reversion
          latestPOIs.current =
            latestPOIs.current?.map((p) =>
              p.extId === poiId ? { ...p, mapData: poiMapData } : p,
            ) || [];

          // Store pending update and show confirmation modal
          isLocationModalOpenRef.current = true;
          setPendingLocationUpdate({
            propId,
            latLng: { lng: Number(latLng.lng), lat: Number(latLng.lat) },
            data: poiMapData,
            isPOI: true,
          });
          setShowLocationModal(true);
        }
      } else {
        const property = latestProperty?.current?.find(
          (each) => each.propertyId === propId,
        );
        const bamMapData = JSON.parse(property?.bamMapData || "{}");
        const data = {
          ...bamMapData,
          pdfCoordinates: {
            longitude: latLng.lng,
            latitude: latLng.lat,
          },
        };

        // Update Redux state immediately to keep marker at new position
        dispatch(
          updatePropertyAction({
            propertyId: propId,
            updates: { bamMapData: JSON.stringify(data) },
          }),
        );

        // Also update latestProperty ref immediately to prevent marker reversion
        latestProperty.current =
          latestProperty.current?.map((prop) =>
            prop.propertyId === propId
              ? { ...prop, bamMapData: JSON.stringify(data) }
              : prop,
          ) || [];

        // Update surveyRef to keep it in sync
        if (surveyRef.current) {
          surveyRef.current = {
            ...surveyRef.current,
            properties: latestProperty.current,
          };
        }

        // Store pending update and show confirmation modal
        // Set ref FIRST to block map updates synchronously before async state updates
        isLocationModalOpenRef.current = true;
        setPendingLocationUpdate({
          propId,
          latLng: { lng: Number(latLng.lng), lat: Number(latLng.lat) },
          data,
        });
        setShowLocationModal(true);
      }
    };

    const handleMarkerClick = async (
      marker: CustomMarker,
      eventType: string,
    ) => {
      const property = latestProperty?.current?.find(
        (each) => each.propertyId === marker.getElement().id,
      );
      const points: MapPoints = (await mapUtil.getMapPointsWithImages(
        surveyRef?.current!,
        selectMediaFiles ?? undefined,
        leaseComps,
        leaseCompMediaFiles,
      )) as MapPoints;

      // Add POI features to the points
      if (isIncludePOI && poiPoints && poiCategories) {
        const poiFeatures = mapUtil.createPOIFeatures(poiPoints, poiCategories);
        points.features.push(...poiFeatures);
      }

      setMarkerInstance(marker);

      if (points?.features?.length) {
        setMarkerPoint(
          points.features.find(
            (each: any) => each.id === marker.getElement().id,
          ),
        );

        // Clear any old selectedPinInfo before opening the drawer
        // The useEffect will read fresh data from sessionStorage if available
        setSelectedPinInfo(null);

        setOpenMapStyleDrawer(true);
        if (eventType === "style") {
          if (property?.isClientShortlist) return;
          setIsMapStyle(true);
        } else {
          setIsMapStyle(false);
        }
      }
    };

    const handleEditClick = (id: string, propertyType?: string) => {
      if (propertyType === "leaseComp") {
        dispatch(setOpenLeaseCompsDrawer(true));
        dispatch(setCurrentLeaseCompId({ leaseCompId: id }));
      } else if (propertyType === "poi") {
        const poiId = id.replace("poi-", "");
        dispatch(setOpenPOIsDrawer(true));
        dispatch(currentPoiId({ poiId }));
      } else {
        dispatch(setOpenPropertiesDrawer(true));
        dispatch(currentPropertyIdAction({ propertyId: id }));
      }
    };

    const handleToggleClick = async (
      marker: CustomMarker,
      propertyType?: string,
    ) => {
      const points: MapPoints = (await mapUtil.getMapPointsWithImages(
        surveyRef.current!,
        selectMediaFiles ?? undefined,
        leaseComps,
        leaseCompMediaFiles,
      )) as MapPoints;

      // Add POI features to the points
      if (isIncludePOI && poiPoints && poiCategories) {
        const poiFeatures = mapUtil.createPOIFeatures(poiPoints, poiCategories);
        points.features.push(...poiFeatures);
      }

      if (points?.features?.length) {
        const markerPoints = points.features.find(
          (each) => each.id === marker.getElement().id,
        );
        const {
          geometry,
          properties: markerProperties,
          id,
        } = markerPoints as {
          geometry: any;
          properties: any;
          id: string;
        };
        markerPoints || {};
        const { coordinates } = geometry || {};
        const { markerConfig } = markerProperties;
        const { radius } = markerConfig || {};
        if (radius?.length && mapRef.current) {
          mapUtil.removeRadiusRings(mapRef?.current, coordinates, id);
        }
        const { _element: element } = marker;
        element.remove();
        mapRef?.current?._markers?.forEach((each: mapboxgl.Marker) => {
          marker.getElement().id === each.getElement().id && each.remove();
        });
        setIsMapEdit(false);
        if (propertyType === "leaseComp") {
          const leaseComp = leaseComps?.find(
            (each) => each.leaseCompId === marker.getElement().id,
          );
          if (!leaseComp) return;
          updateVisibility({
            leaseCompId: marker.getElement().id,
            isHidden: true,
            surveyId: leaseComp.surveyId,
          });
        } else if (propertyType === "poi") {
          // POI removal - just remove from map, no backend update needed yet
          console.log("POI hidden:", marker.getElement().id);
          // TODO: Implement POI hide/unhide with backend API when available
        } else {
          dispatch(
            updatePropertyAction({
              propertyId: marker.getElement().id,
              updates: { isHidden: true },
            }),
          );
          updateProperty({
            propertyId: marker.getElement().id,
            updates: {
              isHidden: true,
            },
          });
        }
      }
    };

    mapRef.current?._markers?.forEach((marker: mapboxgl.Marker) => {
      const { _popup: popup } = marker;
      popup?.on("open", () => {
        const propertyType = marker.getElement().dataset.propertyType;
        mapUtil.handleMarkerSelection(marker);

        // Shift popup upward if it overlaps with the map key section
        requestAnimationFrame(() => {
          const popupEl = marker._popup?.getElement();
          const mapKeyEl = document.getElementById("map-key-section");
          if (popupEl && mapKeyEl) {
            const popupRect = popupEl.getBoundingClientRect();
            const mapKeyRect = mapKeyEl.getBoundingClientRect();
            const overlap = popupRect.bottom > mapKeyRect.top;
            if (overlap) {
              const shift = popupRect.bottom - mapKeyRect.top + 8;
              const current = parseFloat(popupEl.style.top || "0") || 0;
              popupEl.style.top = `${current - shift}px`;
            }
          }
        });

        const popupContent = marker._popup?.getElement();
        const styleElement = popupContent?.querySelector(
          "#utility-style-property",
        );
        const radiusElement = popupContent?.querySelector(
          "#utility-radius-property",
        );
        const editElement = popupContent?.querySelector(
          "#utility-edit-property",
        );
        const toggleElement = popupContent?.querySelector(
          "#utility-toggle-property",
        );
        const editPOIElement = popupContent?.querySelector("#utility-edit-poi");
        const togglePOIElement = popupContent?.querySelector(
          "#utility-toggle-poi",
        );

        const handleMarkerClickWrapper = () => {
          marker._popup?.remove();
          handleMarkerClick(marker as CustomMarker, "style");
        };
        styleElement?.addEventListener("click", handleMarkerClickWrapper);

        const handleRadiusClickWrapper = () => {
          marker._popup?.remove();
          handleMarkerClick(marker as CustomMarker, "radius");
        };
        radiusElement?.addEventListener("click", handleRadiusClickWrapper);

        const handleEditClickWrapper = () => {
          marker._popup?.remove();
          handleEditClick(marker.getElement().id, propertyType);
        };
        editElement?.addEventListener("click", handleEditClickWrapper);

        const handleToggleClickWrapper = () => {
          marker._popup?.remove();
          handleToggleClick(marker as CustomMarker, propertyType);
        };
        toggleElement?.addEventListener("click", handleToggleClickWrapper);

        const handleEditPOIWrapper = () => {
          marker._popup?.remove();
          handleEditClick(marker.getElement().id, "poi");
        };
        editPOIElement?.addEventListener("click", handleEditPOIWrapper);

        const handleTogglePOIWrapper = async () => {
          marker._popup?.remove();
          const poiId = marker.getElement().id.replace("poi-", "");

          if (!survey?.id) return;

          try {
            await updatePOI({
              surveyId: survey.id,
              poiId: poiId,
              body: { isHidden: true },
            }).unwrap();

            // Remove marker from map
            marker.remove();
          } catch (error) {
            console.error("Failed to hide POI:", error);
          }
        };
        togglePOIElement?.addEventListener("click", handleTogglePOIWrapper);

        marker._popup?.on("close", () => {
          mapUtil.removeMarkerSelection(
            marker,
            surveyRef.current?.properties || [],
          );
          styleElement?.removeEventListener("click", handleMarkerClickWrapper);
          radiusElement?.removeEventListener("click", handleRadiusClickWrapper);
          editElement?.removeEventListener("click", handleEditClickWrapper);
          toggleElement?.removeEventListener("click", handleToggleClickWrapper);
          editPOIElement?.removeEventListener("click", handleEditPOIWrapper);
          togglePOIElement?.removeEventListener(
            "click",
            handleTogglePOIWrapper,
          );
        });
      });

      marker.on("dragend", async () => {
        const points: MapPoints = (await mapUtil.getMapPointsWithImages(
          surveyRef.current!,
          selectMediaFiles ?? undefined,
          leaseComps,
          leaseCompMediaFiles,
        )) as MapPoints;

        // Add POI features to the points for drag handling
        if (isIncludePOI && poiPoints && poiCategories) {
          const poiFeatures = mapUtil.createPOIFeatures(
            poiPoints,
            poiCategories,
          );
          points.features.push(...poiFeatures);
        }

        if (points?.features?.length) {
          const markerPoints = points.features.find(
            (each) => each.id === marker.getElement().id,
          );
          const {
            geometry,
            properties: markerProperties,
            id,
          } = markerPoints as { geometry: any; properties: any; id: string };
          const { coordinates } = geometry || {};
          const { markerConfig, propertyType } = markerProperties;
          const { radius, radiusColor, radiusLength, radiusLabel } =
            markerConfig || {};
          if (radius?.length && mapRef.current) {
            mapUtil.removeRadiusRings(mapRef?.current, coordinates, id);
          }

          const propId = marker.getElement().id;
          const latLng = marker.getLngLat();
          if (radius?.length && mapRef.current) {
            radius?.forEach((each: RadiusObj, radiusIndex: number) => {
              mapUtil.addRadiusRings(
                mapRef.current!,
                [latLng.lng, latLng.lat],
                each.value,
                radiusColor,
                radiusLength,
                radiusLabel,
                propId,
                radiusIndex,
              );
            });
          }

          setIsMapEdit(true);
          await mapStateUpdated(propId, latLng, propertyType);
        }
      });
    });
  };

  const updateMapView = (newCenter: [number, number], newZoom: number) => {
    const map = mapRef?.current;
    if (!map) return;

    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();

    // Only update if center or zoom actually changed (with small tolerance for floating point)
    const centerChanged =
      Math.abs(currentCenter.lng - newCenter[0]) > 0.000001 ||
      Math.abs(currentCenter.lat - newCenter[1]) > 0.000001;
    const zoomChanged = Math.abs(currentZoom - newZoom) > 0.01;

    if (centerChanged || zoomChanged) {
      if (centerChanged) map.setCenter(newCenter);
      if (zoomChanged) map.setZoom(newZoom);
    }
  };

  const fitBoundsForMapReset = (force = false) => {
    // Prevent concurrent fitBounds operations unless this call is triggered
    // from an idle listener after a previous recenter request.
    if (!force && isFitBoundsInProgressRef.current) {
      return;
    }

    isFitBoundsInProgressRef.current = true;

    const { _markers: markers } = mapRef.current as any;

    const bounds = new mapboxgl.LngLatBounds();
    const uniqueCoords = new Map<string, mapboxgl.LngLat>();
    let uniqueMarkerCount = 0;
    let polygonCoordinateCount = 0;

    markers?.forEach((marker: CustomMarker) => {
      const coord = marker.getLngLat();
      const coordKey = marker.getElement().id;

      // Check if this is a POI marker
      if (coordKey.startsWith("poi-")) {
        // Only include POIs if isIncludePOI is true
        if (!isIncludePOI) {
          return;
        }

        const poiId = coordKey.replace("poi-", "");
        const poi = poiPoints?.find((p) => p.extId === poiId);

        // Skip if POI doesn't exist or is hidden
        if (!poi || poi.isHidden) {
          return;
        }

        // Include POI in bounds
        if (!uniqueCoords.has(coordKey)) {
          uniqueMarkerCount++;
        }
        uniqueCoords.set(coordKey, coord);
        return;
      }

      // Find the property for this marker
      const property = latestProperty?.current?.find((prop) =>
        coordKey.includes(prop.propertyId),
      );

      // Skip if property doesn't exist (deleted) or is hidden
      if (!property || property.isHidden) {
        if (property?.isHidden) {
          return;
        } else {
          const leaseProperty = leaseComps?.find((comp) =>
            coordKey.includes(comp.leaseCompId),
          );
          if (!leaseProperty || leaseProperty.isHidden) {
            return;
          }
        }
      }

      if (!uniqueCoords.has(coordKey)) {
        uniqueMarkerCount++;
      }
      uniqueCoords.set(coordKey, coord);
    });

    uniqueCoords.forEach((coord) => {
      bounds.extend(coord);
    });

    if (shouldRenderPolygons) {
      polygonsRef.current.forEach((polygon) => {
        polygon.coordinates.forEach((ring) => {
          ring.forEach((coordinate) => {
            bounds.extend([coordinate[0], coordinate[1]]);
            polygonCoordinateCount++;
          });
        });
      });
    }

    if (
      (uniqueCoords.size > 0 || polygonCoordinateCount > 0) &&
      mapRef.current
    ) {
      // Adjust padding to account for bottom sidebar (150x80px)
      // Add extra bottom padding to shift the center point up
      const { width: mapKeyWidth, height: mapKeyHeight } = isIncludeMapKey
        ? mapUtil.getMapKeyRect()
        : { width: 0, height: 0 };
      const mapKeyPadding =
        mapKeyHeight > mapKeyWidth
          ? { bottom: 50, left: 50 + mapKeyWidth }
          : { bottom: 50 + mapKeyHeight, left: 50 };

      let timeoutId: number | undefined;
      const map = mapRef.current;

      const onMoveEnd = () => {
        isFitBoundsInProgressRef.current = false;
        map?.off("moveend", onMoveEnd);
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
      };

      timeoutId = window.setTimeout(() => {
        isFitBoundsInProgressRef.current = false;
        map?.off("moveend", onMoveEnd);
      }, 1000);

      map?.on("moveend", onMoveEnd);
      map?.fitBounds(bounds, {
        padding: { top: 50, right: 50, ...mapKeyPadding },
        maxZoom: 18,
      });
    } else {
      isFitBoundsInProgressRef.current = false;
    }
  };

  const registerIdleFitBoundsListener = (mapComp: mapboxgl.Map) => {
    if (idleListenerRef.current) {
      const previousMap = mapRef.current;
      if (previousMap && previousMap !== mapComp) {
        previousMap.off("idle", idleListenerRef.current);
      }
      mapComp.off("idle", idleListenerRef.current);
    }

    const idleListener = () => {
      if (idleListenerRef.current === idleListener) {
        idleListenerRef.current = null;
      }
      mapComp.off("idle", idleListener);
      fitBoundsForMapReset(true);
    };

    idleListenerRef.current = idleListener;
    mapComp.once("idle", idleListener);
  };

  const fetchGeoJson = async () => {
    try {
      setPortsData(marinePortsRes?.features || []);
      if (mapRef.current) {
        mapUtil.addMarinePortLayer(
          mapRef.current,
          marinePortsRes?.features || [],
          survey.isMarinePorts ?? false,
        );
        mapUtil.raisePolygonLayers(mapRef.current);
      }
    } catch (error) {
      console.error("Failed to fetch marine ports data");
    }
  };

  const onRecenterChange = () => {
    setIsMapEdit(true);

    fitBoundsForMapReset();
    const mapData = { ...marketMapData, mapReset: true };
    setMarketMapData(mapData);
    if (index === 0) {
      dispatch(
        updateCurrentSurvey({ marketMapBamData: JSON.stringify(mapData) }),
      );
      updateSurveyFlags({
        id: survey.id,
        marketMapBamData: JSON.stringify(mapData),
      }).unwrap();
    }
  };

  const onMapStyleChange = (style: string) => {
    const map = mapRef.current;
    const setStyle =
      style === BAM_MAP_STYLES.emeraldBingHybrid
        ? bingMap
        : BAM_MAP_STYLE_SOURCES[style];
    if (setStyle) {
      map?.setStyle(setStyle);
    }
    if (index === 0) {
      dispatch(updateCurrentSurvey({ marketMapStyle: style }));
      updateSurveyFlags({
        id: survey.id,
        marketMapStyle: style,
      }).unwrap();
    }

    map?.once("idle", () => {
      updateMapPoints();
      mapUtil.addMarinePortLayer(
        map,
        portsData || [],
        survey.isMarinePorts ?? false,
      );
      mapUtil.raisePolygonLayers(map);
    });
  };

  const clearPdfCoords = () => {
    const movedProperties = mapUtil.getMovedPropertyPinIds(survey.properties);
    const movedLeaseComps = mapUtil.getMovedLeaseCompPinIds(leaseComps || []);
    const movedPOIs = mapUtil.getMovedPOIPinIds(poiPoints || []);

    const modifiedProps =
      movedProperties.length > 0
        ? latestProperty?.current?.map((prop) => {
            const bamMapData = JSON.parse(prop.bamMapData || "{}");
            if (!bamMapData?.pdfCoordinates) return prop;
            const data = { ...bamMapData, pdfCoordinates: {} };
            if (index === 0) {
              dispatch(
                updatePropertyAction({
                  propertyId: prop.propertyId,
                  updates: { bamMapData: JSON.stringify(data) },
                }),
              );
              updateProperty({
                propertyId: prop.propertyId,
                updates: { bamMapData: JSON.stringify(data) },
              });
            }
            return { ...prop, bamMapData: JSON.stringify(data) };
          })
        : latestProperty?.current;

    const modifiedLeaseComps =
      movedLeaseComps.length > 0
        ? leaseComps?.map((comp) => {
            if (!comp.mapData?.latitude && !comp.mapData?.longitude)
              return comp;
            // Strip only the coordinate override; preserve all style fields
            // (iconColor, iconSize, radius, etc.) so they are not lost on reset.
            const {
              latitude: _lat,
              longitude: _lng,
              ...styleOnlyMapData
            } = (comp.mapData || {}) as Record<string, unknown>;
            if (index === 0) {
              updateLeaseComp({
                leaseCompId: comp.leaseCompId,
                updates: { mapData: styleOnlyMapData },
                surveyId: comp.surveyId,
              });
            }
            return { ...comp, mapData: styleOnlyMapData };
          })
        : leaseComps;

    const modifiedPOIs =
      movedPOIs.length > 0
        ? latestPOIs.current?.map((poi) => {
            if (!poi.mapData?.latitude && !poi.mapData?.longitude) return poi;
            if (index === 0 && survey?.id) {
              updatePOI({
                surveyId: survey.id,
                poiId: poi.extId,
                body: { mapData: {} },
              });
            }
            return { ...poi, mapData: {} };
          })
        : latestPOIs.current;

    if (movedProperties.length > 0) {
      dispatch(
        updateCurrentSurvey({
          properties: modifiedProps,
          marketMapBamData: JSON.stringify({
            ...marketMapData,
            mapReset: true,
          }),
        }),
      );
    }

    // Update latestPOIs ref
    if (movedPOIs.length > 0 && modifiedPOIs) {
      latestPOIs.current = modifiedPOIs;
    }

    const mapComp = mapRef.current;
    const modifiedSurvey = {
      ...surveyRef.current,
      properties: modifiedProps || [],
      marketMapBamData: JSON.stringify({ ...marketMapData, mapReset: true }),
    } as DetailedSurvey;

    if (mapComp && modifiedSurvey) {
      setPinsDragged(false);
      updateMapPoints(modifiedSurvey, undefined, modifiedLeaseComps);
    }
  };

  const closeMap = () => {
    setOpenMapStyleDrawer(false);
    setIsMapStyle(false);
    setSelectedPinInfo(null);
    // Clear any remaining sessionStorage data
    sessionStorage.removeItem("selectedPinInfo");
  };

  /**
   * Unified map key updater — called after any pin color change (single or bulk).
   *
   * Decides whether to UPDATE an existing map key entry or CREATE a new one:
   *  • UPDATE: every visible pin that carried `oldColor` is being changed
   *            (remainingWithOldColor === 0) — safe to repurpose the entry.
   *  • CREATE: some pins still keep `oldColor`, so the existing entry must stay;
   *            a brand-new entry is created for `newColor`.
   *  • SKIP:   `newColor` already has a map key entry — nothing needed.
   *
   * `mapKeysRef.current` is kept in sync after each call so that repeated
   * invocations within the same drag session don't create duplicates.
   */
  const applyMapKeyChange = useCallback(
    (
      oldColor: string,
      newColor: string,
      remainingWithOldColor: number,
      category: "Property" | "LeaseComp" | undefined,
    ) => {
      if (oldColor.toLowerCase() === newColor.toLowerCase()) return;

      const matchesCategory = (key: MapKeyType) =>
        category === "LeaseComp"
          ? key.category === "LeaseComp"
          : key.category === "Property" || !key.category;

      const oldMapKey = mapKeysRef.current?.find(
        (k) =>
          k.color?.toLowerCase() === oldColor.toLowerCase() &&
          matchesCategory(k),
      );
      if (!oldMapKey) return;

      // If the new color already has a map key entry, no action needed.
      const newColorExists = mapKeysRef.current?.some(
        (k) =>
          k.color?.toLowerCase() === newColor.toLowerCase() &&
          matchesCategory(k),
      );
      if (newColorExists) return;

      if (remainingWithOldColor === 0) {
        // All visible pins of this color are changing → update the existing entry.
        const updatedMapKey = { ...oldMapKey, color: newColor };
        updateSurveyMapKey({
          mapKeyId: oldMapKey.mapKeyId!,
          mapKey: updatedMapKey,
          surveyId: survey?.id || "",
        });
        dispatch(updateToggleLabelMapKey(updatedMapKey));
        mapKeysRef.current = (mapKeysRef.current ?? []).map((k) =>
          k.mapKeyId === oldMapKey.mapKeyId ? updatedMapKey : k,
        );
      } else {
        // Some pins keep the old color → create a new entry for the new color.
        const newEntry: MapKeyType = {
          label: oldMapKey.label,
          color: newColor,
          active: true,
          isCurrentLocation: oldMapKey.isCurrentLocation,
          order: (oldMapKey.order ?? 0) + 1,
          category: oldMapKey.category,
        };
        addSurveyMapKeys({
          surveyId: surveyRef.current?.id ?? "",
          mapKeys: [newEntry],
        });
        // Optimistically update ref to prevent duplicate creation on repeated calls.
        mapKeysRef.current = [...(mapKeysRef.current ?? []), newEntry];
      }
    },
    [], // all dependencies are stable refs or stable RTK hooks
  );

  const updatePropertyStyle = useCallback(
    (propertyId: string, iconSize: number, iconColor: string) => {
      const property = latestProperty?.current?.find(
        (each) => each.propertyId === propertyId,
      );
      if (property) {
        const bamMapData = JSON.parse(property?.bamMapData || "{}");
        const previousIconColor = mapUtil.getIconData(property).iconColor;

        // When the color is changing, check if the old color is exclusively used
        // by this single property — if so, update the corresponding map key too
        if (previousIconColor.toLowerCase() !== iconColor.toLowerCase()) {
          const remainingWithOldColor = (latestProperty.current ?? []).filter(
            (p) =>
              !p.isHidden &&
              p.propertyId !== propertyId &&
              (p.isCurrentLocation ?? false) ===
                (property.isCurrentLocation ?? false) &&
              mapUtil.getIconData(p).iconColor.toLowerCase() ===
                previousIconColor.toLowerCase(),
          ).length;
          applyMapKeyChange(
            previousIconColor,
            iconColor,
            remainingWithOldColor,
            "Property",
          );
        }

        const data = {
          ...bamMapData,
          iconSize,
          iconColor,
        };
        dispatch(
          updatePropertyAction({
            propertyId,
            updates: { bamMapData: JSON.stringify(data) },
          }),
        );
        updateProperty({
          propertyId,
          updates: {
            bamMapData: JSON.stringify(data),
          },
        });
      }
    },
    [], // Empty dependencies - use refs to access latest values
  );

  const debouncedUpdatePropertyStyle = useMemo(
    () => debounce(updatePropertyStyle, 1000),
    [updatePropertyStyle],
  );

  const updateLeaseCompStyle = useCallback(
    (leaseCompId: string, iconSize: number, iconColor: string) => {
      const leaseComp = latestLeaseComps.current?.find(
        (each) => each.leaseCompId === leaseCompId,
      );
      if (leaseComp) {
        const previousIconColor = leaseComp.mapData?.iconColor || "#E47444";

        // When color changes, update the corresponding map key
        if (previousIconColor.toLowerCase() !== iconColor.toLowerCase()) {
          const matchingMapKey = mapKeys?.find(
            (key) =>
              key.color?.toLowerCase() === previousIconColor.toLowerCase() &&
              key.category === "LeaseComp",
          );
          // Only update if no other lease comps share the old color
          const otherLeaseCompsWithSameColor = latestLeaseComps.current?.filter(
            (lc) =>
              !lc.isHidden &&
              lc.leaseCompId !== leaseCompId &&
              (lc.mapData?.iconColor || "#E47444").toLowerCase() ===
                previousIconColor.toLowerCase(),
          );
          if (
            matchingMapKey?.mapKeyId &&
            (!otherLeaseCompsWithSameColor ||
              otherLeaseCompsWithSameColor.length === 0)
          ) {
            const updatedMapKey = { ...matchingMapKey, color: iconColor };
            updateSurveyMapKey({
              mapKeyId: updatedMapKey.mapKeyId!,
              mapKey: updatedMapKey,
              surveyId: survey?.id || "",
            });
            dispatch(updateToggleLabelMapKey(updatedMapKey));
          }
        }

        const data = {
          ...leaseComp.mapData,
          iconSize,
          iconColor,
        };
        dispatch(
          updateLeaseCompInStore({
            leaseCompId,
            updates: { mapData: data },
          }),
        );
        updateLeaseComp({
          leaseCompId,
          updates: { mapData: data },
          surveyId: leaseComp.surveyId,
        })
          .unwrap()
          .catch(() => {
            // Mutation errors are already handled by RTK Query.
          });
      }
    },
    [mapKeys, survey.id],
  );

  const debouncedUpdateLeaseCompStyle = useMemo(
    () => debounce(updateLeaseCompStyle, 1000),
    [updateLeaseCompStyle],
  );

  // Helper function to generate SVG marker
  const generateMarkerSVG = (
    iconColor: string,
    iconSize: number,
    textSize: string,
    textContent: string,
    icon: string,
  ): SVGSVGElement => {
    const safeColor = /^#[0-9a-fA-F]{3,8}$/.test(iconColor)
      ? iconColor
      : "#000000";
    const safeSize =
      Number.isFinite(iconSize) && iconSize > 0 ? Math.round(iconSize) : 24;
    const safeFontSize = Number.isFinite(Number(textSize))
      ? Math.round(Number(textSize))
      : 32;
    const safeText = String(textContent ?? "").replace(/[<>&"']/g, "");
    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("width", String(safeSize));
    svg.setAttribute("height", String(safeSize));
    svg.setAttribute("viewBox", "0 0 64 64");
    svg.setAttribute("xmlns", svgNS);

    if (icon === "starIcon") {
      const circle = document.createElementNS(svgNS, "circle");
      circle.setAttribute("cx", "32");
      circle.setAttribute("cy", "32");
      circle.setAttribute("r", "28");
      circle.setAttribute("fill", safeColor);
      circle.setAttribute("stroke", "#fff");
      circle.setAttribute("stroke-width", "5");

      const polygon = document.createElementNS(svgNS, "polygon");
      polygon.setAttribute("fill", "#ffffff");
      polygon.setAttribute(
        "points",
        "45.57 51.2 32 43.04 18.46 51.2 22.05 35.81 10.11 25.6 25.86 24.26 32 9.6 38.14 24.1 53.89 25.44 41.95 35.78 45.57 51.2",
      );

      svg.appendChild(circle);
      svg.appendChild(polygon);
    } else if (icon === "squareIcon") {
      const rect = document.createElementNS(svgNS, "rect");
      rect.setAttribute("x", "4");
      rect.setAttribute("y", "4");
      rect.setAttribute("width", "56");
      rect.setAttribute("height", "56");
      rect.setAttribute("fill", safeColor);
      rect.setAttribute("stroke", "#fff");
      rect.setAttribute("stroke-width", "5");

      const text = document.createElementNS(svgNS, "text");
      text.setAttribute("x", "50%");
      text.setAttribute("y", "50%");
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("dy", ".4em");
      text.setAttribute("fill", "#fff");
      text.setAttribute("font-size", `${safeFontSize}px`);
      text.textContent = safeText;

      svg.appendChild(rect);
      svg.appendChild(text);
    } else {
      const circle = document.createElementNS(svgNS, "circle");
      circle.setAttribute("cx", "32");
      circle.setAttribute("cy", "32");
      circle.setAttribute("r", "29");
      circle.setAttribute("fill", safeColor);
      circle.setAttribute("stroke", "#fff");
      circle.setAttribute("stroke-width", "5");

      const text = document.createElementNS(svgNS, "text");
      text.setAttribute("x", "50%");
      text.setAttribute("y", "50%");
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("dy", ".4em");
      text.setAttribute("fill", "#fff");
      text.setAttribute("font-size", `${safeFontSize}px`);
      text.textContent = safeText;

      svg.appendChild(circle);
      svg.appendChild(text);
    }

    return svg;
  };

  const updateMapStyles = (styles: { color: string; size: number }) => {
    // LAYER 1 & 3: Set guards BEFORE any updates to prevent map viewport changes
    isStyleEditInProgressRef.current = true;
    setIsMapEdit(true);
    dispatch(setStyleEditInProgress(true));

    const { color: iconColor, size: iconSize } = styles;

    // Check if we're doing bulk styling for multiple pins
    if (
      selectedPinInfo &&
      selectedPinInfo.ids &&
      selectedPinInfo.ids.length >= 1
    ) {
      // Bulk styling mode — update all selected pins simultaneously
      const selectedIds = new Set(selectedPinInfo.ids);

      // Capture old colors BEFORE eagerly updating refs so we can compute
      // how many non-selected pins still carry each old color.
      const propertyOldColors = new Map<string, string>(); // propertyId → oldColor
      for (const p of latestProperty.current ?? []) {
        if (selectedIds.has(p.propertyId) && !p.isHidden) {
          propertyOldColors.set(p.propertyId, mapUtil.getIconData(p).iconColor);
        }
      }
      const leaseCompOldColors = new Map<string, string>(); // leaseCompId → oldColor
      for (const lc of leaseCompsRef.current ?? []) {
        if (selectedIds.has(lc.leaseCompId)) {
          leaseCompOldColors.set(
            lc.leaseCompId,
            lc.mapData?.iconColor ?? "#E47444",
          );
        }
      }

      // Eagerly update refs so individual style calls below see
      // previousIconColor === iconColor and skip their own map key logic.
      leaseCompsRef.current = leaseCompsRef.current?.map((lc) =>
        selectedIds.has(lc.leaseCompId)
          ? { ...lc, mapData: { ...lc.mapData, iconSize, iconColor } }
          : lc,
      );
      latestProperty.current = (latestProperty.current ?? []).map((p) => {
        if (!selectedIds.has(p.propertyId)) return p;
        const bam = JSON.parse(p.bamMapData || "{}");
        return {
          ...p,
          bamMapData: JSON.stringify({ ...bam, iconSize, iconColor }),
        };
      });

      const updatePromises = selectedPinInfo.ids.map((pinId) => {
        return new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            const el = document.getElementById(pinId);
            if (!el) {
              resolve();
              return;
            }

            // data-property-type is set via el.dataset.propertyType in map.ts
            const propertyType = el.dataset.propertyType;

            // Read the current label from the existing SVG before clearing so
            // the visible pin number is preserved exactly as displayed
            const existingLabel = el.querySelector("text")?.textContent ?? "";

            const markerConfig = {
              textSize: "32",
              textContent: existingLabel,
              icon:
                propertyType === "leaseComp"
                  ? "squareIcon"
                  : latestProperty?.current?.find((p) => p.propertyId === pinId)
                        ?.isCurrentLocation
                    ? "starIcon"
                    : "",
            };

            el.replaceChildren();
            el.appendChild(
              generateMarkerSVG(
                iconColor,
                iconSize || 30,
                markerConfig.textSize,
                markerConfig.textContent,
                markerConfig.icon,
              ),
            );

            if (propertyType === "leaseComp") {
              updateLeaseCompStyle(pinId, iconSize, iconColor);
            } else {
              updatePropertyStyle(pinId, iconSize, iconColor);
            }
            resolve();
          });
        });
      });

      Promise.all(updatePromises).then(() => {
        mapRef.current?.triggerRepaint();

        // Update map key for each unique old color among the selected pins.
        // applyMapKeyChange decides whether to UPDATE (all pins changed) or
        // CREATE (partial change, new color has no existing map key entry).
        const seenPropertyColors = new Set<string>();
        for (const oldColor of propertyOldColors.values()) {
          if (seenPropertyColors.has(oldColor)) continue;
          seenPropertyColors.add(oldColor);
          const remaining = (latestProperty.current ?? []).filter(
            (p) =>
              !selectedIds.has(p.propertyId) &&
              !p.isHidden &&
              mapUtil.getIconData(p).iconColor.toLowerCase() ===
                oldColor.toLowerCase(),
          ).length;
          applyMapKeyChange(oldColor, iconColor, remaining, "Property");
        }

        const seenLeaseCompColors = new Set<string>();
        for (const oldColor of leaseCompOldColors.values()) {
          if (seenLeaseCompColors.has(oldColor)) continue;
          seenLeaseCompColors.add(oldColor);
          const remaining = (leaseCompsRef.current ?? []).filter(
            (lc) =>
              !selectedIds.has(lc.leaseCompId) &&
              (lc.mapData?.iconColor ?? "#E47444").toLowerCase() ===
                oldColor.toLowerCase(),
          ).length;
          applyMapKeyChange(oldColor, iconColor, remaining, "LeaseComp");
        }
      });
    } else {
      // Single pin styling mode (original behavior)
      const { id: propertyId, properties: markerProperties } =
        markerPoint || {};
      const { markerConfig, propertyType } = markerProperties || {};
      const { textSize, textContent, icon } = markerConfig || {};
      const el = markerInstance?.getElement();

      if (el) {
        el.replaceChildren();
        const svg = generateMarkerSVG(
          iconColor,
          iconSize || 0,
          textSize,
          textContent,
          icon,
        );
        el.appendChild(svg);
      }

      // Update backend (debounced for single mode)
      // Map key update is handled inside updatePropertyStyle (after debounce)
      // using resolveMapKeyColorUpdate with the original latestProperty state.
      if (propertyType === "leaseComp") {
        // Eagerly update the ref and Redux *before* the debounced API call fires.
        // This guarantees that mapStateUpdated (triggered by an immediate drag)
        // reads the new iconColor/iconSize from latestLeaseComps.current instead
        // of the stale mapData, preventing an empty mapData in the PATCH body.
        const leaseComp = latestLeaseComps.current?.find(
          (lc) => lc.leaseCompId === propertyId,
        );
        if (leaseComp) {
          const updatedMapData = { ...leaseComp.mapData, iconSize, iconColor };
          latestLeaseComps.current = latestLeaseComps.current?.map((lc) =>
            lc.leaseCompId === propertyId
              ? { ...lc, mapData: updatedMapData }
              : lc,
          );
          dispatch(
            updateLeaseCompInStore({
              leaseCompId: propertyId,
              updates: { mapData: updatedMapData },
            }),
          );
        }
        debouncedUpdateLeaseCompStyle(propertyId, iconSize, iconColor);
      } else {
        debouncedUpdatePropertyStyle(propertyId, iconSize, iconColor);
      }
    }

    // Clear guards after debounce completes + buffer for Redux/React updates
    // 1000ms (debounce) + 500ms (buffer) = 1500ms
    setTimeout(() => {
      isStyleEditInProgressRef.current = false;
      setIsMapEdit(false);
      dispatch(setStyleEditInProgress(false));
    }, 1500);
  };

  const updatePropertyRadius = (
    propertyId: string,
    radius: RadiusObj[],
    radiusColor: string,
    radiusLabel: boolean,
    radiusLength: string,
  ) => {
    const property = latestProperty?.current?.find(
      (each) => each.propertyId === propertyId,
    );
    if (property) {
      const bamMapData = JSON.parse(property?.bamMapData || "{}");
      const data = {
        ...bamMapData,
        radius,
        radiusColor,
        radiusLabel,
        radiusLength,
      };
      dispatch(
        updatePropertyAction({
          propertyId,
          updates: { bamMapData: JSON.stringify(data) },
        }),
      );
      updateProperty({
        propertyId,
        updates: {
          bamMapData: JSON.stringify(data),
        },
      });
    }
  };

  const debouncedUpdatePropertyRadius = useMemo(
    () => debounce(updatePropertyRadius, 1000),
    [updatePropertyRadius],
  );

  const updateLeaseCompRadius = useCallback(
    (
      leaseCompId: string,
      radius: RadiusObj[],
      radiusColor: string,
      radiusLabel: boolean,
      radiusLength: string,
    ) => {
      const leaseComp = latestLeaseComps.current?.find(
        (each) => each.leaseCompId === leaseCompId,
      );
      if (leaseComp) {
        const data = {
          ...leaseComp.mapData,
          radius,
          radiusColor,
          radiusLabel,
          radiusLength,
        };
        dispatch(
          updateLeaseCompInStore({
            leaseCompId,
            updates: { mapData: data },
          }),
        );
        updateLeaseComp({
          leaseCompId,
          updates: { mapData: data },
          surveyId: leaseComp.surveyId,
        })
          .unwrap()
          .catch(() => {
            // Mutation errors are already handled by RTK Query.
          });
      }
    },
    [survey.id],
  );

  const debouncedUpdateLeaseCompRadius = useMemo(
    () => debounce(updateLeaseCompRadius, 1000),
    [updateLeaseCompRadius],
  );

  const updateRadiusRings = (rings: {
    radiusLabel: boolean;
    radiusColor: string;
    radiusLength: string;
    radius: RadiusObj[];
  }) => {
    // LAYER 1 & 3: Set guards to prevent map viewport changes during radius updates
    isStyleEditInProgressRef.current = true;
    setIsMapEdit(true);
    dispatch(setStyleEditInProgress(true));

    const { radiusLabel, radiusColor, radiusLength, radius } = rings;
    const {
      geometry,
      id: propertyId,
      properties: markerProperties,
    } = markerPoint || {};
    const { propertyType } = markerProperties || {};
    const { coordinates } = geometry || {};
    mapRef.current &&
      mapUtil.removeRadiusRings(mapRef.current, coordinates, propertyId);
    if (radius?.length) {
      radius.forEach((each: RadiusObj, radiusIndex: number) => {
        mapRef.current &&
          mapUtil.addRadiusRings(
            mapRef.current,
            coordinates,
            each.value,
            radiusColor,
            radiusLength,
            radiusLabel,
            propertyId,
            radiusIndex,
          );
      });
    }
    if (propertyType === "leaseComp") {
      debouncedUpdateLeaseCompRadius(
        propertyId,
        radius,
        radiusColor,
        radiusLabel,
        radiusLength,
      );
    } else {
      debouncedUpdatePropertyRadius(
        propertyId,
        radius,
        radiusColor,
        radiusLabel,
        radiusLength,
      );
    }

    // Clear guards after debounce completes + buffer
    setTimeout(() => {
      isStyleEditInProgressRef.current = false;
      setIsMapEdit(false);
      dispatch(setStyleEditInProgress(false));
    }, 1500);
  };

  const setLayerVisability = () => {
    const visibility = survey.isMarinePorts ? "visible" : "none";
    if (mapRef?.current?.getLayer("marine-port-layer")) {
      mapRef.current.setLayoutProperty(
        "marine-port-layer",
        "visibility",
        visibility,
      );
    }
  };

  useEffect(() => {
    setLayerVisability();
  }, [survey?.isMarinePorts]);

  const visiblePropertiesLength =
    survey?.properties?.filter((p) => !p.isHidden).length ?? 0;
  const visibleLeaseCompsLength =
    leaseComps?.filter((c) => !c.isHidden).length ?? 0;

  useEffect(() => {
    setTimeout(() => {
      const map = mapRef.current;
      map && map.getContainer() && map?.resize();
    }, 50);
  }, [
    survey?.pdfPaperSize,
    survey?.isIncludeHeader,
    survey?.customLegends?.displayFields,
    visiblePropertiesLength,
    visibleLeaseCompsLength,
  ]);

  // ResizeObserver effect - responds to actual map container size changes
  useEffect(() => {
    const mapContainer = mapRef.current?.getContainer();
    if (!mapContainer) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const debounceResize = <T extends (...args: any[]) => void>(
      fn: T,
      delay: number,
    ) => {
      return ((...args: Parameters<T>) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          timeoutId = undefined;
          fn(...args);
        }, delay);
      }) as T;
    };

    const handleResize = debounceResize(() => {
      const map = mapRef.current;
      if (map && map.getContainer()) {
        map.resize();
      }
    }, 50);

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(mapContainer);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const handleCloseLocationModal = () => {
    isLocationModalOpenRef.current = false;
    setShowLocationModal(false);
    setPendingLocationUpdate(null);
  };

  const handleUpdateCoordinates = () => {
    if (pendingLocationUpdate) {
      const { propId, latLng, data, isLeaseComp, isPOI } =
        pendingLocationUpdate;

      if (isPOI) {
        const poiId = propId.replace("poi-", "");
        const poi = latestPOIs.current?.find((p) => p.extId === poiId);
        if (poi && survey?.id) {
          // Clear mapData and update actual lat/lng coordinates
          const poiUpdates = {
            mapData: {},
            latitude: String(latLng.lat),
            longitude: String(latLng.lng),
          };

          // Sync Redux state
          dispatch(
            updatePOIInStore({
              extId: poiId,
              updates: poiUpdates,
            }),
          );

          // Sync latestPOIs ref
          latestPOIs.current =
            latestPOIs.current?.map((p) =>
              p.extId === poiId ? { ...p, ...poiUpdates } : p,
            ) || [];

          // Persist to API
          updatePOI({
            surveyId: survey.id,
            poiId: poiId,
            body: poiUpdates,
          });

          // Hide reset icon — web coordinates now match map position
          setPinsDragged(false);
        }
      } else if (isLeaseComp) {
        // Use the ref so we get the most up-to-date mapData even if the
        // debounced style API call hasn't fired yet (latestLeaseComps.current
        // is eagerly updated in updateMapStyles on every style change).
        const leaseComp = latestLeaseComps.current?.find(
          (each) => each.leaseCompId === propId,
        );
        if (leaseComp) {
          // Strip coordinate-override fields; keep all style fields
          // (iconColor, iconSize, radius, etc.).
          const {
            latitude: _lat,
            longitude: _lng,
            ...styleOnlyMapData
          } = (leaseComp.mapData || {}) as Record<string, unknown>;
          const leaseCompUpdates = {
            mapData: styleOnlyMapData, // style preserved; no coordinate override
            leaseCompFields: {
              latitude: String(latLng.lat),
              longitude: String(latLng.lng),
            },
          };

          // Sync Redux state
          dispatch(
            updateLeaseCompInStore({
              leaseCompId: propId,
              updates: leaseCompUpdates,
            }),
          );

          // Persist to API (invalidates cache → triggers GET /leaseComp/$surveyId/all)
          updateLeaseComp({
            leaseCompId: propId,
            updates: leaseCompUpdates,
            surveyId: leaseComp.surveyId,
          });

          // Hide reset icon — web coordinates now match map position
          setPinsDragged(false);
        }
      } else {
        // 1. Reset bamMapData by removing pdfCoordinates (same as reset action)
        const { pdfCoordinates: _removed, ...cleanedBamMapData } = data;
        const cleanedBamMapDataStr = JSON.stringify(cleanedBamMapData);

        // 2. Updates to apply: cleaned bamMapData + new lat/lng baked in
        const propertyUpdates = {
          bamMapData: cleanedBamMapDataStr,
          latitude: String(latLng.lat),
          longitude: String(latLng.lng),
        };

        // Sync Redux state
        dispatch(
          updatePropertyAction({
            propertyId: propId,
            updates: propertyUpdates,
          }),
        );

        // Sync latestProperty ref
        latestProperty.current =
          latestProperty.current?.map((prop) =>
            prop.propertyId === propId ? { ...prop, ...propertyUpdates } : prop,
          ) || [];

        // Sync surveyRef
        if (surveyRef.current) {
          surveyRef.current = {
            ...surveyRef.current,
            properties: latestProperty.current,
          };
        }

        // Persist to API
        updateProperty({
          propertyId: propId,
          updates: propertyUpdates,
        });

        // Force immediate map refresh — isMapEdit is still true from the drag
        // so the reactive useEffect would be blocked; call directly instead.
        setIsMapEdit(false);
        updateMapPoints(surveyRef.current);

        // 3. Revert the reset icon state
        setPinsDragged(false);
      }
    }
    handleCloseLocationModal();
  };

  const handleUpdatePdfOnly = () => {
    if (pendingLocationUpdate) {
      const { propId, data, isLeaseComp, isPOI } = pendingLocationUpdate;

      if (isPOI) {
        const poiId = propId.replace("poi-", "");
        if (survey?.id) {
          // Persist mapData with new lat/lng — PDF-only position override
          updatePOI({
            surveyId: survey.id,
            poiId: poiId,
            body: { mapData: data },
          });
          // Keep pinsDragged true so the reset icon remains visible
        }
      } else if (isLeaseComp) {
        const leaseComp = leaseComps?.find(
          (each) => each.leaseCompId === propId,
        );
        if (leaseComp) {
          // Persist mapData with new lat/lng — PDF-only position override
          // (invalidates cache → triggers GET /leaseComp/$surveyId/all)
          updateLeaseComp({
            leaseCompId: propId,
            updates: { mapData: data },
            surveyId: leaseComp.surveyId,
          });
          // Keep pinsDragged true so the reset icon remains visible
        }
      } else {
        // 1. Persist the new pdfCoordinates in bamMapData to the API
        updateProperty({
          propertyId: propId,
          updates: { bamMapData: JSON.stringify(data) },
        });

        // 2. Keep pinsDragged true so the reset icon remains visible
      }
    }
    handleCloseLocationModal();
  };

  // Polygon Drawing Handlers — use refs to avoid stale closures so these
  // callbacks are stable and only registered once on the map.
  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!isPolygonDrawingModeRef.current) return;

      const target = e.originalEvent?.target as HTMLElement | null;
      if (
        target?.closest(".mapboxgl-marker") ||
        target?.closest(".marker-pin") ||
        target?.closest('[class^="marker-"]') ||
        target?.closest(".mapboxgl-popup")
      ) {
        return;
      }

      const clickCount = (e.originalEvent as MouseEvent | undefined)?.detail ?? 1;
      // Ignore the second click event that belongs to a dblclick gesture.
      if (clickCount > 1) return;

      const { lng, lat } = e.lngLat;
      const newPoint: number[] = [lng, lat];
      // Update the synchronous ref immediately (before React re-renders)
      drawingPointsRef.current = [...drawingPointsRef.current, newPoint];
      setCurrentDrawingPolygon([...drawingPointsRef.current]);
    },
    [],
  );

  const handleMapDoubleClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      // Prevent the default double-click zoom from also firing
      e.preventDefault();
      if (!isPolygonDrawingModeRef.current) return;

      const points = drawingPointsRef.current;

      if (points.length < 3) return;

      // Close the polygon and save it
      const polygonCoordinates = [[...points, points[0]]];

      const currentPolygons = polygonsRef.current;
      const surveyId = surveyRef.current?.id;
      if (!surveyId) return;
      const tempId = `polygon-${Date.now()}`;

      const newPolygon: MapPolygon = {
        polygonId: tempId,
        surveyId,
        name: `Polygon ${currentPolygons.length + 1}`,
        showLabel: true,
        showBorder: true,
        coordinates: polygonCoordinates,
        style: getNextPolygonStyle(currentPolygons),
        isHidden: false,
        order: currentPolygons.length,
        createdOn: new Date().toISOString(),
        updatedOn: new Date().toISOString(),
      };

      dispatch(addPolygon(newPolygon));
      drawingPointsRef.current = [];
      setCurrentDrawingPolygon([]);
      dispatch(setPolygonDrawingMode(false));

      const postPromise = createPolygon({
        surveyId,
        body: {
          name: newPolygon.name,
          showLabel: newPolygon.showLabel,
          showBorder: newPolygon.showBorder,
          coordinates: newPolygon.coordinates,
          style: newPolygon.style,
          isHidden: newPolygon.isHidden,
          order: newPolygon.order,
        },
      })
        .unwrap()
        .then((savedPolygon) => {
          dispatch(
            updatePolygonInStore({
              polygonId: tempId,
              updates: savedPolygon,
            }),
          );
          pendingPostsRef.current.delete(tempId);
          return savedPolygon.polygonId;
        })
        .catch((err: unknown) => {
          dispatch(deletePolygonFromStore(tempId));
          pendingPostsRef.current.delete(tempId);
          throw err;
        });

      pendingPostsRef.current.set(tempId, postPromise);
    },
    [createPolygon, dispatch],
  );

  const handleDrawingMouseMove = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!isPolygonDrawingModeRef.current) return;

      const lastPoint =
        drawingPointsRef.current[drawingPointsRef.current.length - 1];
      const cursorLineSource = mapRef.current?.getSource(
        "drawing-cursor-line",
      ) as mapboxgl.GeoJSONSource | undefined;
      const drawingPolygonSource = mapRef.current?.getSource(
        "drawing-polygon",
      ) as mapboxgl.GeoJSONSource | undefined;

      if (!lastPoint || !cursorLineSource || !drawingPolygonSource) return;

      const cursorPoint: number[] = [e.lngLat.lng, e.lngLat.lat];

      cursorLineSource.setData({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [lastPoint, cursorPoint],
        },
        properties: {},
      });

      const drawingPoints = drawingPointsRef.current;
      drawingPolygonSource.setData({
        type: "Feature",
        geometry:
          drawingPoints.length < 2
            ? {
                type: "LineString",
                coordinates: [lastPoint, cursorPoint],
              }
            : {
                type: "Polygon",
                coordinates: [[...drawingPoints, cursorPoint, drawingPoints[0]]],
              },
        properties: {},
      });
    },
    [],
  );

  const handlePolygonClick = useCallback(
    (polygonId: string) => {
      // If user clicks a polygon while drawing, stop drawing and open style drawer.
      if (isPolygonDrawingModeRef.current) {
        dispatch(setPolygonDrawingMode(false));
        drawingPointsRef.current = [];
        setCurrentDrawingPolygon([]);
      }

      const polygon = polygonsRef.current.find((p) => p.polygonId === polygonId);
      if (polygon) {
        setSelectedPolygon(polygon);
        setIsPolygonStyleOpen(true);
        dispatch(setCurrentPolygonId(polygonId));
      }
    },
    [dispatch],
  );

  const debouncedPatchPolygonStyle = useMemo(
    () =>
      debounce(
        ({
          surveyId,
          polygonId,
          updates,
        }: {
          surveyId: string;
          polygonId: string;
          updates: Partial<MapPolygon>;
        }) => {
          const pendingPost = pendingPostsRef.current.get(polygonId);
          if (pendingPost) {
            // POST is still in-flight — wait for the real polygonId then PATCH
            pendingPost
              .then((realPolygonId) => {
                patchPolygon({ surveyId, polygonId: realPolygonId, body: updates })
                  .unwrap()
                  .catch(() => {});
              })
              .catch(() => {
                // POST failed; polygon removed from store, nothing to PATCH
              });
            return;
          }
          patchPolygon({
            surveyId,
            polygonId,
            body: updates,
          })
            .unwrap()
            .catch(() => {
              // Sync failure is non-critical; live UI state is already updated in Redux.
            });
        },
        600,
      ),
    [patchPolygon],
  );

  useEffect(() => {
    return () => {
      debouncedPatchPolygonStyle.cancel();
    };
  }, [debouncedPatchPolygonStyle]);

  const handlePolygonStyleSave = useCallback(
    (
      polygonId: string,
      style: PolygonStyle,
      name?: string,
      showLabel?: boolean,
      showBorder?: boolean,
    ) => {
      const existing = polygonsRef.current.find((p) => p.polygonId === polygonId);
      const surveyId = surveyRef.current?.id;
      if (!existing || !surveyId) return;

      const updates: Partial<MapPolygon> = {
        style,
        name,
        showLabel,
        showBorder,
        updatedOn: new Date().toISOString(),
      };

      dispatch(
        updatePolygonInStore({
          polygonId,
          updates,
        }),
      );

      debouncedPatchPolygonStyle({
        surveyId,
        polygonId,
        updates,
      });
    },
    [debouncedPatchPolygonStyle, dispatch],
  );

  const handlePolygonDelete = useCallback(
    (polygonId: string) => {
      const surveyId = surveyRef.current?.id;
      const existing = polygonsRef.current.find((p) => p.polygonId === polygonId);
      if (!surveyId || !existing) return;

      shouldRecenterAfterPolygonDeleteRef.current = true;
      dispatch(deletePolygonFromStore(polygonId));
      setIsPolygonStyleOpen(false);

      removePolygon({ surveyId, polygonId })
        .unwrap()
        .catch(() => {
          dispatch(addPolygon(existing));
        });
    },
    [dispatch, removePolygon],
  );

  // Reliable polygon selection via map-level hit-testing.
  // This avoids missing layer-bound click handlers after style reloads.
  const handleMapPolygonSelect = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (isPolygonDrawingModeRef.current) return;
      if (!shouldRenderPolygons || !mapRef.current) return;
      if (!mapRef.current.isStyleLoaded()) return;

      const target = e.originalEvent?.target as HTMLElement | null;
      if (
        target?.closest(".mapboxgl-marker") ||
        target?.closest(".marker-pin") ||
        target?.closest('[class^="marker-"]') ||
        target?.closest(".mapboxgl-popup")
      ) {
        return;
      }

      const features = mapRef.current.queryRenderedFeatures(e.point, {
        layers: ["polygons-fill", "polygons-outline", "polygons-label"],
      });

      const polygonId = features?.[0]?.properties?.polygonId;
      if (polygonId) {
        handlePolygonClick(polygonId);
      }
    },
    [handlePolygonClick, shouldRenderPolygons],
  );

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    map.on("click", handleMapPolygonSelect);

    return () => {
      if (!map) return;
      map.off("click", handleMapPolygonSelect);
    };
  }, [handleMapPolygonSelect]);

  // Add polygon layers to map
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const syncPolygonLayers = () => {
      mapUtil.syncPolygonLayers({
        map,
        polygons,
        shouldRenderPolygons,
        currentDrawingPolygon,
        defaultPolygonStyle: DEFAULT_POLYGON_STYLE,
        polygonMouseEnterHandlerRef,
        polygonMouseLeaveHandlerRef,
        polygonMouseLeaveCursorRef,
      });
    };

    const handleStyleSync = () => {
      syncPolygonLayers();
    };

    // Initial sync attempt + future syncs when style finishes loading/reloading.
    handleStyleSync();
    map.on("styledata", handleStyleSync);
    map.on("load", handleStyleSync);

    return () => {
      // Check if map still exists and hasn't been removed
      if (!map) return;
      map.off("styledata", handleStyleSync);
      map.off("load", handleStyleSync);
    };
  }, [polygons, currentDrawingPolygon, shouldRenderPolygons, index]);

  useEffect(() => {
    if (!shouldRenderPolygons) return;

    if (shouldRecenterAfterPolygonDeleteRef.current) {
      shouldRecenterAfterPolygonDeleteRef.current = false;
      fitBoundsForMapReset(true);
      return;
    }

    if (polygons.length > 0) fitBoundsForMapReset(true);
  }, [polygons, shouldRenderPolygons]);

 // Keep drawing handlers attached for the map lifetime. The callbacks use refs
  // to ignore events outside drawing mode, so viewport updates cannot detach them.
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    map.on("click", handleMapClick);
    map.on("dblclick", handleMapDoubleClick);
    map.on("mousemove", handleDrawingMouseMove);

    return () => {
      map.off("click", handleMapClick);
      map.off("dblclick", handleMapDoubleClick);
      map.off("mousemove", handleDrawingMouseMove);
    };
  }, [
    handleMapClick,
    handleMapDoubleClick,
    handleDrawingMouseMove,
    index,
  ]);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    map.getCanvas().style.cursor = polygonMouseLeaveCursorRef.current;

    if (isPolygonDrawingMode) {
      map.doubleClickZoom.disable();
    } else {
      if (index === 0) map.doubleClickZoom.enable();
      else map.doubleClickZoom.disable();
      drawingPointsRef.current = [];
      setCurrentDrawingPolygon([]);
    }

    return () => {
      if (mapRef.current !== map) return;
      map.getCanvas().style.cursor = "";
      if (index === 0) map.doubleClickZoom.enable();
    };
  }, [isPolygonDrawingMode, index]);


  return (
    <>
      <div
        id={`marketmap-${index}`}
        ref={mapContainerRef}
        className={`h-full w-full ${index !== 0 ? "[&_.mapboxgl-marker]:pointer-events-none" : ""}`}
      >
        {index === 0 && (
          <div data-html2canvas-ignore="true">
            {pinsDragged && <MapRestore onRestoreChange={clearPdfCoords} />}
            <MapCenter onRecenterChange={onRecenterChange} />
            <MapStyles onMapStyleChange={onMapStyleChange} />
          </div>
        )}
        {isIncludeMapKey && <MapKey />}
      </div>
      <MapStyleDrawer
        openMap={openMapStyleDrawer}
        key={openMapStyleDrawer ? "open" : "close"}
        isMapStyle={isMapStyle}
        closeMap={closeMap}
        markerPoint={markerPoint}
        updateMapStyles={updateMapStyles}
        updateRadiusRings={updateRadiusRings}
      />

      <LocationAdjustmentModal
        isOpen={showLocationModal}
        pendingUpdate={pendingLocationUpdate}
        onClose={handleCloseLocationModal}
        onUpdateCoordinates={handleUpdateCoordinates}
        onUpdatePdfOnly={handleUpdatePdfOnly}
      />

      <PolygonStyleDrawer
        isOpen={isPolygonStyleOpen}
        polygon={selectedPolygon}
        onClose={() => {
          setIsPolygonStyleOpen(false);
          dispatch(setCurrentPolygonId(null));
        }}
        onSave={handlePolygonStyleSave}
        onDelete={handlePolygonDelete}
      />
    </>
  );
};

export default MarketMapPreview;
